import type { MouseEvent, Ref } from 'react';
import type { SampleDataFieldError } from '../../../types/sampleData';
import { describeField, fieldId } from './templateEditing';

interface SampleDataErrorSummaryProps {
  errors: SampleDataFieldError[];
  ref?: Ref<HTMLDivElement>;
}

/**
 * A list-level path such as `duplicatePairPlayerKeys` has no input of its own, so it falls back to
 * the first control of that group.
 */
const findField = (path: string): HTMLElement | null =>
  document.getElementById(fieldId(path)) ?? document.getElementById(fieldId(`${path}[0]`));

const focusField = (event: MouseEvent<HTMLAnchorElement>, path: string) => {
  const target = findField(path);
  if (!target) return;
  event.preventDefault();
  target.focus();
};

/**
 * Every validation failure in one list at the top of the form. Focused when it appears, so a
 * keyboard or screen reader user lands on what went wrong instead of on a Save button far below it.
 * Each item links to its field, where the same message is shown inline.
 */
export const SampleDataErrorSummary = ({ errors, ref }: SampleDataErrorSummaryProps) => (
  <div
    ref={ref}
    className="sample-data__summary"
    tabIndex={-1}
    aria-labelledby="sample-data-summary-title"
    data-testid="sample-data-error-summary"
  >
    <h3 className="sample-data__summary-title" id="sample-data-summary-title">
      The template was not saved. Fix these and save again.
    </h3>
    <ul className="sample-data__summary-list">
      {errors.map((error, index) => (
        <li key={`${error.field}-${index}`}>
          {error.field ? (
            <a
              className="sample-data__summary-link"
              href={`#${fieldId(error.field)}`}
              onClick={(event) => focusField(event, error.field)}
            >
              <strong>{describeField(error.field)}:</strong> {error.message}
            </a>
          ) : (
            error.message
          )}
        </li>
      ))}
    </ul>
  </div>
);
